import { useState } from 'react';
import {
  Checkbox, FormControl, Grid, InputLabel, ListItemText, MenuItem, Select, TextField, Typography
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Dialog, OfficialAvatar, SearchableAgencyOfficialDropdownOption } from '@components';
import {
  Operation, OperationTeam, OperationRole, UpdateOperationOfficialData, updateOperationOfficials
} from '../../../../../networkRequest/operationsService';
import Utils from '../../../../../utils/utils';
import { useUser } from '@hooks/useUser';

interface AddParticipantFormData {
  team_ids?: string[];
  assignment?: string;
}

interface AddParticipantDialogProps {
  open: boolean;
  onClose: () => void;
  onOperationUpdate: (op: Operation) => void;
  operation: Operation;
  operationRoles: OperationRole[];
  selectedAgencyOfficial: SearchableAgencyOfficialDropdownOption | null;
  teams: OperationTeam[];
}

const AddParticipantDialog = ({
  operation,
  operationRoles,
  selectedAgencyOfficial,
  open,
  onClose,
  onOperationUpdate,
  teams,
}: AddParticipantDialogProps) => {
  if (!selectedAgencyOfficial) {
    return null;
  }
  const theme = useTheme();
  const { user } = useUser();

  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<AddParticipantFormData>({});

  const handleSave = async () => {
    if (!user?.accessToken || !operation?.id || !selectedAgencyOfficial || !formData) return;

    try {
      const participantRoleId = operationRoles.find((r) => r.name === 'Participant')?.id || '';

      const payload: UpdateOperationOfficialData[] = [{
        agency_official_id: Number(selectedAgencyOfficial.id),
        assignment: formData.assignment ?? '',
        operation_role_id: participantRoleId,
        team_assignments: formData?.team_ids?.map((id) => ({ team_id: id })) ?? []
      }];

      const result = await updateOperationOfficials({
        accessToken: user.accessToken,
        operationId: operation.id,
        operationOfficialData: payload
      });

      if (result.error) {
        setError(result.error);
        return;
      }

      if (result.data) {
        onOperationUpdate(result.data);
      }

      onClose();
    } catch (err: any) {
      setError('Could not add participant.');
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      primaryButtonAction={handleSave}
      primaryButtonDisabled={Boolean(error)}
      title='Add Participant'
    >
      <Grid container spacing={3} sx={{
        flexDirection: 'column',
        mt: 2
      }}>
        <Grid container sx={{ backgroundColor: theme.palette.grey[50], p: '16px' }}>
          <OfficialAvatar img_url={selectedAgencyOfficial.img_url} size='large' />
          <Grid>
            <Typography variant='body1'>
              {Utils.fullName(selectedAgencyOfficial?.first_name, selectedAgencyOfficial?.last_name)}
            </Typography>
            <Typography variant='body2'>
              {selectedAgencyOfficial.agency_name}
            </Typography>
            <Typography variant='body2'>
              {selectedAgencyOfficial.rank}
            </Typography>
            <Typography variant='body2'>
              {selectedAgencyOfficial.title}
            </Typography>
            <Typography variant='body2'>
              {selectedAgencyOfficial.badge_number}
            </Typography>
          </Grid>
        </Grid>
        <Typography>Specify {Utils.fullName(selectedAgencyOfficial?.first_name, selectedAgencyOfficial?.last_name)}'s team and assignment.</Typography>
        <FormControl variant='outlined'>
          <InputLabel id='team-multi-select'>Team</InputLabel>
          <Select
            labelId="team-multi-select"
            label="Team"
            multiple
            value={formData.team_ids ?? []}
            onChange={(e) => {
              setFormData({
                ...formData,
                team_ids: typeof e.target.value === 'string'
                  ? [e.target.value]
                  : e.target.value,
              });
            }}
            renderValue={(selected) =>
              teams
                .filter((t) => (selected as string[]).includes(t.id))
                .map((t) => t.name)
                .join(', ')
            }
          >
            {teams.length ? (
              teams.map((team) => (
                <MenuItem key={team.id} value={team.id}>
                  <Checkbox checked={(formData.team_ids ?? []).includes(team.id)} />
                  <ListItemText primary={team.name} />
                </MenuItem>
              ))
            ) : (
              <MenuItem disabled>First add a team</MenuItem>
            )}
          </Select>
        </FormControl>
        <TextField
          fullWidth
          variant='outlined'
          size='medium'
          label='Operation Assignment'
          value={formData.assignment ?? ''}
          onChange={(e) => {
            setFormData({
              ...formData,
              assignment: e.target.value
            })
          }}
        />
        {
          error ? <Typography color='error'>{error}</Typography> : null
        }
      </Grid>
    </Dialog>
  );
};

export default AddParticipantDialog;